import { Address, rpc, xdr } from '@stellar/stellar-sdk';

import { ASSET_CONTRACT_METHOD } from 'constants/soroban';
import { ACCOUNT_FOR_SIMULATE } from 'constants/stellar-accounts';
import { TOKEN_BALANCE_STORAGE } from 'constants/token-balance-storage';

import chunkFunction from 'helpers/chunk-function';
import { getNetworkPassphrase } from 'helpers/env';

import type Connection from 'services/soroban/connection/connection';
import { buildBatchCall, simulateBatch } from 'services/soroban/utils/batchSimulation';
import { i128ToInt } from 'services/soroban/utils/scValHelpers';

import { Token, TokenType } from 'types/token';

type Entry = rpc.Api.LedgerEntryResult;
const TOKENS_PER_LEDGER_BATCH = Math.floor(200 / 3);

const ledgerKey = (contract: string, key: xdr.ScVal) =>
    xdr.LedgerKey.contractData(
        new xdr.LedgerKeyContractData({
            contract: Address.fromString(contract).toScAddress(),
            key,
            durability: xdr.ContractDataDurability.persistent,
        }),
    );

const balanceKey = (symbol: string, owner: xdr.ScVal) =>
    xdr.ScVal.scvVec([xdr.ScVal.scvSymbol(symbol), owner]);

// Read the executable and both supported keys in the SAME RPC snapshot. This
// avoids applying an old storage layout to a contract upgraded between requests.
const keysForToken = (contract: string, owner: xdr.ScVal) => [
    ledgerKey(contract, xdr.ScVal.scvLedgerKeyContractInstance()),
    ledgerKey(contract, balanceKey('Balance', owner)),
    ledgerKey(contract, balanceKey('bal', owner)),
];

const readBalance = (
    token: Token,
    owner: xdr.ScVal,
    entries: Map<string, Entry>,
): string | undefined => {
    const [instanceKey] = keysForToken(token.contract, owner);
    const entry = entries.get(instanceKey.toXDR('base64'));
    // RPC includes hot-archive entries. These reviewed accessors return the same
    // stored amount after automatic restoration; TTL does not change the balance.
    // A missing instance still requires simulation, never an assumed zero.
    if (!entry) return undefined;
    const value = entry.val.type === 'contractData' ? entry.val.contractData.val : null;
    if (value?.type !== 'scvContractInstance') return undefined;
    const instance = value.instance;
    if (instance.executable.type !== 'contractExecutableWasm') return undefined;
    const layout = TOKEN_BALANCE_STORAGE[instance.executable.wasmHash.toXDR('hex')];
    if (!layout) return undefined;

    const key = balanceKey(layout.symbol, owner);
    let balance: xdr.ScVal | undefined;
    if (layout.storage !== 'instance') {
        const stored = entries.get(ledgerKey(token.contract, key).toXDR('base64'));
        if (stored) {
            if (stored.val.type !== 'contractData') {
                return undefined;
            }
            balance = stored.val.contractData.val;
        }
    }
    if (!balance && layout.storage !== 'persistent') {
        balance = instance.storage?.find(
            item => item.key.toXDR('base64') === key.toXDR('base64'),
        )?.val;
    }
    // Only a recognised executable may interpret an absent entry as zero.
    if (!balance) return '0';
    if (balance.type !== 'scvI128') return undefined;
    return i128ToInt(balance, token.decimal ?? 7);
};

export default class TokenBalances {
    private pending = new Map<string, Promise<string | null>>();

    constructor(
        private readonly connection: Connection,
        // A short list already fits in one simulation. Avoid an extra RPC round
        // trip on today's mainnet; use ledger reads as the token list grows.
        private readonly ledgerReadThreshold = 30,
    ) {}

    get(tokens: Token[], where: string): Promise<Map<string, string | null>> {
        const network = getNetworkPassphrase();
        const unique = [...new Map(tokens.map(token => [token.contract, token])).values()];
        const requestKey = (token: Token) =>
            `${network}:${where}:${token.contract}:${token.decimal}`;
        const missing = unique.filter(token => !this.pending.has(requestKey(token)));
        if (missing.length) {
            const request = this.load(missing, where).then(balances => {
                if (network !== getNetworkPassphrase()) throw new Error('Network changed');
                return balances;
            });
            missing.forEach(token => {
                const key = requestKey(token);
                const promise = request
                    .then(balances => balances.get(token.contract))
                    .finally(() => {
                        if (this.pending.get(key) === promise) this.pending.delete(key);
                    });
                this.pending.set(key, promise);
            });
        }
        return Promise.all(
            unique.map(
                async token => [token.contract, await this.pending.get(requestKey(token))] as const,
            ),
        ).then(values => new Map(values));
    }

    private async load(tokens: Token[], where: string): Promise<Map<string, string | null>> {
        const owner = Address.fromString(where).toScVal();
        const balances = new Map<string, string | null>();
        const soroban =
            tokens.length > this.ledgerReadThreshold
                ? tokens.filter(token => token.type === TokenType.soroban)
                : [];
        const chunks: Token[][] = [];
        // Three keys per contract, never more than the RPC's 200-key limit.
        for (let i = 0; i < soroban.length; i += TOKENS_PER_LEDGER_BATCH) {
            chunks.push(soroban.slice(i, i + TOKENS_PER_LEDGER_BATCH));
        }
        await chunkFunction(
            chunks,
            async chunk => {
                try {
                    const response = await this.connection.getLedgerEntries(
                        ...chunk.flatMap(token => keysForToken(token.contract, owner)),
                    );
                    const entries = new Map(
                        response.entries.map(entry => [entry.key.toXDR('base64'), entry]),
                    );
                    chunk.forEach(token => {
                        const balance = readBalance(token, owner, entries);
                        if (balance !== undefined) balances.set(token.contract, balance);
                    });
                } catch {
                    // A failed RPC batch uses the existing simulation path, never zero balances.
                }
            },
            3,
        );

        const fallback = tokens.filter(token => !balances.has(token.contract));
        if (!fallback.length) return balances;
        const retvals = await simulateBatch(
            ACCOUNT_FOR_SIMULATE,
            fallback.map(token =>
                buildBatchCall(token.contract, ASSET_CONTRACT_METHOD.GET_BALANCE, [owner]),
            ),
            // The remaining custom accessors can be expensive. Spread a small
            // remainder over the existing three workers instead of one long simulation.
            { maxCallsPerBatch: tokens.length > 30 && fallback.length <= 30 ? 10 : 30 },
        );
        fallback.forEach((token, index) =>
            balances.set(
                token.contract,
                retvals[index] ? i128ToInt(retvals[index], token.decimal ?? 7) : null,
            ),
        );
        return balances;
    }
}
